import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDocument, getCollection, api } from "../config";
import {donationStatus, convertDateTZ, constants} from "../../services/utilities";
import moment from "moment";
import numeral from "numeral";
export const loadRequest = createAsyncThunk(
    "donation/loadRequest",
    async (payload, { getState, rejectWithValue, dispatch }) => {
        try{
            const {account} = getState();
            let rez = await getDocument("donations", payload, [
                {
                  key: "businessUserID",
                  collection: "users_public",
                  field: "businessUserPublic",
                },
                {
                  key: "userID",
                  collection: "users",
                  field: "user",
                },
                {
                  key: "orgID",
                  collection: "user_organizations",
                  field: "org",
                },
                {
                  key: "donationTypeID",
                  collection: "donation_types",
                  field: "donationType",
                },
                {
                  key: "locationID",
                  collection: "locations",
                  field: "location",
                },
                {
                  key: "brandID",
                  collection: "brands",
                  field: "brand",
                },
            ]);
            if(rez.status?.pending&&!account.user?.permissions?.admin) dispatch(isReviewing(payload));
            return rez;
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);

export const loadMessages = createAsyncThunk(
  "donation/loadMessages",
  async (payload, { rejectWithValue }) => {
      try{
        let rs = await getCollection("donation_messages", {
          where: [
            ["donationID", "==", payload]
          ],
          order:['date','desc']
        });
        return rs;
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadLocationContacts = createAsyncThunk(
  "donation/loadLocationContacts",
  async (locationID, { rejectWithValue }) => {
    try{
      const result = await api.locationContactsList({locationID});
      return {
        locationID,
        contacts:result?.data?.data?.contacts||[]
      };
    }catch(e){
      return rejectWithValue(e.message);
    }
  },
  {
    condition: (locationID, { getState }) => {
      if(!locationID) return false;
      const cached = getState().donation.locationContactsByLocation[locationID];
      return !cached||!['loading','fulfilled'].includes(cached.status);
    }
  }
);

export const isReviewing = createAsyncThunk(
  "donation/isReviewing",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {account} = getState();
        await api.donationReviewing({donationID:payload,updateBy:account.user.parentUserID?account.user.parentUserID:account.user.uid});
        return;
      }catch(e){
        return rejectWithValue(e.message);
      }
  }
);

export const changeStatus = createAsyncThunk(  "donation/changeStatus",
  async (payload, { dispatch, getState, rejectWithValue }) => {
      try{
        const {account,donation} = getState();
        const {status,reason,date,time,donationValue,recipientDetails,internalDetails,contacts,fulfillDate,fulfillDetails} = payload;
        
        let data = {
          donationID:donation.request.id,
          ['updateBy']:account.user.parentUserID?account.user.parentUserID:account.user.uid
        };

        if((status==='cancelled'||status==='denied')){
          data['reason']=reason||'';
        }

        if(status==='approved'){
          data['dueDate']=moment(date).format(constants.dayFormat);
          if(time&&time.toString().length) data['dueTime']=moment(time).format('LLLL');
          data['dueUnix']=convertDateTZ(moment(moment(date).format('LL') + ' ' + (time&&time.toString().length>0?moment(time).format('HH:00'):' 00:00')),donation.request.location.timezone||moment.tz.guess()).unix();
          data['donationValue']=donationValue||'';
          data['recipientDetails']=recipientDetails||'';
          data['internalDetails']=internalDetails||'';
          data['contacts']=contacts||[];
        }

        if(status==='fulfilled'){
          data['fulfillDate'] = fulfillDate;
          data['fulfillDetails'] = fulfillDetails;
        }

        switch(status){
          case 'fulfilled':
            delete data.updateBy;
            await api.donationFulfill(data);
          break;
          case'cancelled':
            await api.donationCancel(data);
          break;
          case'denied':
            await api.donationDeny(data);
          break;
          case'approved':
            await api.donationApprove(data);
          break;
        }

        dispatch(loadRequest(data.donationID));

        return;
        
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadNotes = createAsyncThunk(
  "donation/loadNotes",
  async (payload, { rejectWithValue }) => {
    try {
      let rs = await getCollection("donation_notes", {
        where: [
          ["donationID", "==", payload],
          ["deleted", "==", false]
        ],
        order: ['created', 'desc']
      });
      return rs || [];
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const saveNote = createAsyncThunk(
  "donation/saveNote",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const {donation} = getState();
      const response = await api.donationNoteCreate({
        donationID: donation.request.id,
        body: payload.body || '',
        files: payload.files || []
      });
      return { id: response.data.id, ...response.data.data };
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const updateNote = createAsyncThunk(
  "donation/updateNote",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.donationNoteUpdate({
        noteID: payload.noteID,
        body: payload.body
      });
      return { id: response.data.id, ...response.data.data };
    } catch(e) {
      return rejectWithValue({ noteID: payload.noteID, message: e.message });
    }
  }
);

export const deleteNote = createAsyncThunk(
  "donation/deleteNote",
  async (noteID, { rejectWithValue }) => {
    try {
      await api.donationNoteDelete({ noteID });
      return noteID;
    } catch(e) {
      return rejectWithValue({ noteID, message: e.message });
    }
  }
);

export const donationSlice = createSlice({
  name: "donation",
  initialState: {
    loading:true,
    loadingError:null,
    loadingMessages:true,
    messagesError:null,
    request:{
        donationType:null
    },
    activeModal:null,
    changeStatusText:null,
    changeStatusError:null,
    changingStatus:false,
    locationContactsByLocation:{},
    notes:[],
    loadingNotes:false,
    notesError:null,
    savingNote:false,
    savingNoteError:null,
    updatingNoteID:null,
    updatingNoteError:null,
    deletingNoteID:null,
    deletingNoteError:null
  },
  reducers: {
    setActiveModal: (state,action) => {
      state.activeModal = action.payload;
    },
    setChangeStatusText: (state,action) => {
      state.changeStatusText = action.payload;
    },
    setChangeStatusError: (state,action) => {
      state.changeStatusError = action.payload;
    },
    setMessages: (state,action) => {
      state.messages = action.payload;
    }
  },
  extraReducers: {
    [loadRequest.fulfilled]: (state, action) => {
        state.locationContactsByLocation = {};
        if(action.payload) {
            state.request = {
                ...action.payload,
                fulfillDate: action.payload.fulfillDate ? moment(action.payload.fulfillDate).format('ddd, MMM D, YYYY') : null,
                fulfillDetails: action.payload.fulfillDetails || null,
                dueDate: action.payload.dueDate ? moment(action.payload.dueDate,constants.dayFormat).format('ddd, MMM D, YYYY') + (action.payload.dueTime ? ' ' + moment(action.payload.dueTime).format('h:mm A') : '') : '',
                donationValue: action.payload.donationValue?numeral(action.payload.donationValue).format("$0,0.00"):'',
                filterStatus:donationStatus(action.payload)
            };
        } else {
            state.loadingError = "Request does not exist."
        }
        state.loading = false;
        return;
    },
    [loadRequest.pending]: (state, action) => {
        state.loading = true;
        return;
    },
    [loadRequest.rejected]: (state, action) => {
        state.loadingError = action.payload;
        state.loading = false;
        return;
    },
    [loadMessages.fulfilled]: (state, action) => {
        state.messages = action.payload;
        state.loadingMessages=false;
        return;
      },
      [loadMessages.pending]: (state, action) => {
        state.messagesError = null;
        state.loadingMessages=true;
        return;
      },
      [loadMessages.rejected]: (state, action) => {
        state.messagesError = action.payload;
        state.loadingMessages=false;
        return;
      },
      [loadLocationContacts.fulfilled]: (state, action) => {
        state.locationContactsByLocation[action.payload.locationID] = {
          status:'fulfilled',
          contacts:action.payload.contacts,
          error:null
        };
        return;
      },
      [loadLocationContacts.pending]: (state, action) => {
        state.locationContactsByLocation[action.meta.arg] = {
          status:'loading',
          contacts:[],
          error:null
        };
        return;
      },
      [loadLocationContacts.rejected]: (state, action) => {
        state.locationContactsByLocation[action.meta.arg] = {
          status:'rejected',
          contacts:[],
          error:action.payload||action.error?.message||'Unable to load location contacts.'
        };
        return;
      },
      [isReviewing.fulfilled]: (state, action) => {
        state.request.filterStatus = 'Reviewing';
        return;
      },
      [changeStatus.fulfilled]: (state, action) => {
        state.changingStatus = false;
        state.activeModal = null;
        return;
      },
      [changeStatus.pending]: (state, action) => {
        state.changeStatusError = null;
        state.changingStatus = true;
        return;
      },
      [changeStatus.rejected]: (state, action) => {
        state.changeStatusError = action.payload;
        state.changingStatus = false;
        return;
      },
      [loadNotes.fulfilled]: (state, action) => {
        state.notes = action.payload;
        state.loadingNotes = false;
        return;
      },
      [loadNotes.pending]: (state, action) => {
        state.notesError = null;
        state.loadingNotes = true;
        return;
      },
      [loadNotes.rejected]: (state, action) => {
        state.notesError = action.payload;
        state.loadingNotes = false;
        return;
      },
      [saveNote.fulfilled]: (state, action) => {
        state.notes.unshift(action.payload);
        state.savingNote = false;
        state.savingNoteError = null;
        return;
      },
      [saveNote.pending]: (state, action) => {
        state.savingNote = true;
        state.savingNoteError = null;
        return;
      },
      [saveNote.rejected]: (state, action) => {
        state.savingNote = false;
        state.savingNoteError = action.payload;
        return;
      },
      [updateNote.fulfilled]: (state, action) => {
        const idx = state.notes.findIndex(n => n.id === action.payload.id);
        if (idx !== -1) state.notes[idx] = action.payload;
        state.updatingNoteID = null;
        state.updatingNoteError = null;
        return;
      },
      [updateNote.pending]: (state, action) => {
        state.updatingNoteID = action.meta.arg.noteID;
        state.updatingNoteError = null;
        return;
      },
      [updateNote.rejected]: (state, action) => {
        state.updatingNoteID = null;
        state.updatingNoteError = action.payload?.message || action.payload;
        return;
      },
      [deleteNote.fulfilled]: (state, action) => {
        state.notes = state.notes.filter(n => n.id !== action.payload);
        state.deletingNoteID = null;
        state.deletingNoteError = null;
        return;
      },
      [deleteNote.pending]: (state, action) => {
        state.deletingNoteID = action.meta.arg;
        state.deletingNoteError = null;
        return;
      },
      [deleteNote.rejected]: (state, action) => {
        state.deletingNoteID = null;
        state.deletingNoteError = action.payload?.message || action.payload;
        return;
      },
  },
});

export const {
    setActiveModal,
    setChangeStatusError,
    setChangeStatusText,
    setMessages
} = donationSlice.actions;
export default donationSlice.reducer;
